// components/TopNav.jsx
import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Stack,
  Box,
  Typography,
  IconButton,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { getIconEl } from "./utils";

/**
 * Responsive navigation bar with drawer on mobile.
 * Props: { navItems: [{ label, to, icon, emphasis }] }
 */
export default function TopNav({ navItems }) {
  const [open, setOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 900px)");
  const { pathname } = useLocation();

  const MenuButtons = (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={{ xs: 0.5, md: 0.25 }}
      sx={{ width: { xs: "100%", md: "auto" }, "& a": { textDecoration: "none" } }}
    >
      {navItems.map((item) =>
        item.emphasis ? (
          <Button
            key={item.to}
            component={RouterLink}
            to={item.to}
            color="secondary"
            variant="contained"
            size="small"
            sx={{ mt: { xs: 1, md: 0 }, minWidth: { xs: "100%", md: "auto" } }}
          >
            {item.label}
          </Button>
        ) : (
          <Button
            key={item.to}
            component={RouterLink}
            to={item.to}
            color="inherit"
            size="small"
            startIcon={item.icon ? getIconEl(item.icon, { fontSize: "small" }) : null}
            aria-current={pathname === item.to ? "page" : undefined}
            sx={{
              justifyContent: { xs: "flex-start", md: "center" },
              color: pathname === item.to ? "primary.light" : "text.secondary",
              bgcolor: pathname === item.to ? "rgba(67,217,231,.08)" : "transparent",
              "&:hover": { color: "text.primary", bgcolor: "rgba(255,255,255,.05)" },
            }}
          >
            {item.label}
          </Button>
        )
      )}
    </Stack>
  );

  return (
    <>
      <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ gap: 1, minHeight: { xs: 60, md: 72 }, maxWidth: 1280, width: "100%", mx: "auto" }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            component={RouterLink}
            to="/"
            sx={{ color: "inherit", textDecoration: "none", flexGrow: { xs: 1, md: 0 } }}
          >
            <Box component="img" src="/logo.png" alt="ARL Logo" sx={{ height: { xs: 40, md: 48 }, width: "auto" }} />
          </Stack>

          {isDesktop ? (
            <Box sx={{ ml: "auto" }}>{MenuButtons}</Box>
          ) : (
            <IconButton edge="end" onClick={() => setOpen(true)} aria-label="open navigation" size="large" sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{ width: "min(86vw, 340px)", p: 2.5, bgcolor: "background.default", height: "100%" }}
          role="presentation"
          onClick={() => setOpen(false)}
          onKeyDown={() => setOpen(false)}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            {getIconEl("SportsMotorsports", { color: "primary" })}
            <Typography variant="h6" sx={{ fontWeight: 800 }}>ARL Menu</Typography>
          </Stack>
          <Divider sx={{ mb: 1 }} />
          <List>
            {navItems.map((item) => (
              <ListItem key={item.to} disablePadding>
                <ListItemButton component={RouterLink} to={item.to} selected={pathname === item.to} sx={{ minHeight: 48, borderRadius: 1.5, mb: .5 }}>
                  {item.icon && <Box sx={{ mr: 1.5, display: "flex" }}>{getIconEl(item.icon, { fontSize: "small" })}</Box>}
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontWeight: item.emphasis ? 800 : 600 }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
}
